/**
 * useChartData Hook
 *
 * Fetches and transforms report data for chart/table rendering in ReportEditorV2.
 * Uses the new /api/chart-data endpoint which fetches directly from BigQuery
 * (doesn't require a cached report to exist).
 */

import { useState, useEffect, useCallback } from 'react';
import { getChartData } from '../services/reportService';
import {
  transformReportData,
  type BackendReportData,
} from '../utils/reportDataTransformers';

export type ChartDataResult = ReturnType<typeof transformReportData>;

interface UseChartDataState {
  data: ChartDataResult | null;
  isLoading: boolean;
  error: string | null;
  reportId: string | null;
}

export function useChartData(institutionId: string, period: string) {
  const [state, setState] = useState<UseChartDataState>({
    data: null,
    isLoading: false,
    error: null,
    reportId: null,
  });

  const fetchChartData = useCallback(async () => {
    if (!institutionId || !period) {
      setState(prev => ({ ...prev, data: null, error: null, isLoading: false }));
      return;
    }

    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      // Fetch chart data directly from BigQuery
      let content;
      try {
        content = await getChartData(institutionId, period);
      } catch (fetchError) {
        // If fetch fails, silently continue without chart data
        // This allows the editor to work even if chart data isn't available
        console.warn('[useChartData] Failed to fetch chart data:', fetchError);
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: null, // Don't show error - charts just won't render
          data: null,
        }));
        return;
      }

      // Transform to chart format
      const backendData: BackendReportData = {
        metadata: content.metadata,
        data: content.data,
        narratives: content.narratives || {},
      };

      const transformed = transformReportData(backendData);

      setState({
        data: transformed,
        isLoading: false,
        error: null,
        reportId: null, // No cached report needed
      });
    } catch (err) {
      // Catch-all for unexpected errors
      console.error('[useChartData] Unexpected error:', err);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: null, // Don't show errors - charts just won't render
        data: null,
      }));
    }
  }, [institutionId, period]);

  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  return {
    ...state,
    refetch: fetchChartData,
  };
}

export default useChartData;
